import { listCategoriesQuery } from "@/api/category";
import {
  isBookingVariant,
  isEapVariantPrice,
  isEventVariant,
  isMarketplaceVariantPrice,
  isOfferVariant,
  isSubscriptionVariant,
  listingQuery,
  registerMembershipTypesQuery,
  registersQuery,
  TagSet,
  tagSetQuery,
  useReviewListingMutation,
} from "@/api/listings";
import { autoMessagesQuery } from "@/api/mgmtMessages";
import {
  calendarModelsQuery,
  isCommissionPricingConfig,
  settingsQuery,
  supplierLocationsQuery,
  supplierQuery,
} from "@/api/supplier";
import { queryClient } from "@/api/utils";
import { TagsDisplay } from "@/components/listing-editor/cards/TagCard";
import { Markdown } from "@/components/markdown/Markdown";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { calculateSalePrice } from "@/constants/eap";
import { DEFAULT_COMMISSION_RATE } from "@/constants/financial";
import { CertificateIcon } from "@/icons";
import { getTokenCurrencyCodes, TokenCurrencyCode, tokensToMoney } from "@/modules/money-fns";
import { VariantTypeBadge } from "@/pages/admin/listing-review/VariantTypeBadge";
import { seo } from "@/utils/seo";
import { config } from "@common/utils/config";
import { centsToDollars, dollarsToCents, getCurrencyDecimalPlaces } from "@common/utils/currency";
import { formatDateTime, toDateOnly } from "@common/utils/date";
import { formatCents } from "@common/utils/i18n";
import {
  Box,
  Field,
  Flex,
  Grid,
  Heading,
  Img,
  Label,
  Select,
  Span,
  Table,
  Tbody,
  Text,
  Thead,
  toast,
  Tr,
} from "@givenwell/components";
import {
  CalendarIcon,
  ChatBubbleTextSquareIcon,
  CheckIcon,
  CircleClockIcon,
  CloseIcon,
  EarthIcon,
  LocationPinIcon,
  MailIcon,
  TagIcon,
} from "@givenwell/core-solid-icons";
import {
  AutoMessageWithAssociationsAndAttachments,
  CitedRegistration,
  GetSupplierListingResponse,
  GetSupplierResponse,
  PricingConfig,
  Register,
  ReviewSalePrice,
  SupplierListingVariant,
  VariantPrice,
} from "@givenwell/management-api";
import { CSS, styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { DeclineListingModal } from "./DeclineListingModal";

const route = getRouteApi("/admin/listing-review/$listingId/$version");

export const Route = createFileRoute("/admin/listing-review/$listingId/$version")({
  component: ListingReviewPageLoader,
  beforeLoad: () => ({
    breadcrumb: "Listing",
  }),
  parseParams: ({ version, ...params }) => {
    const num = +version;
    if (!isFinite(num)) {
      throw new Error("Invalid listing version");
    }
    return {
      ...params,
      version: Math.round(num),
    };
  },
  stringifyParams: ({ version, ...params }) => ({
    ...params,
    version: version.toString(),
  }),
  loader: ({ params }) => {
    queryClient.prefetchQuery(listingQuery(params.listingId, params.version));
  },
  head: () => ({ meta: seo({ title: "Listing Review" }) }),
});

function ListingReviewPageLoader() {
  const params = route.useParams();
  const navigate = useNavigate();

  const reviewMut = useReviewListingMutation();

  const { data: listing } = useSuspenseQuery(listingQuery(params.listingId, params.version));
  const { data: previousListing } = useSuspenseQuery(listingQuery(params.listingId, Math.max(params.version - 1, 1)));
  const hasPreviousVersion = params.version > 1;
  const isReadonly = listing.status !== "pending";

  const { data: supplier } = useSuspenseQuery(supplierQuery(listing.supplier_id));
  const { data: registers } = useSuspenseQuery(registersQuery(supplier.country_code));
  const register = registers.registers?.find(r => r.id === listing.registration?.register_id);

  if (listing.registration && !register) {
    throw new Error("Suppliers Register not found");
  }

  const [citedRegistration, setCitedRegistration] = useState(
    listing.registration ? supplierRegistrationToCitedRegistration(listing.registration, register!) : undefined,
  );

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [prices, setPrices] = useState<DisplayPrice[] | undefined>(undefined);
  const [notifySupplier, setNotifySupplier] = useState(!listing.eap_available);

  function handlePricesUpdated(variantId: string, variantPrices: DisplayPrice[]) {
    setPrices(prices => {
      const pricesForOtherVariants = (prices ?? []).filter(p => p.variantId !== variantId);
      return [...pricesForOtherVariants, ...variantPrices];
    });
  }

  async function handleDeclineConfirmed(reason: string) {
    if (!listing) return;
    await reviewMut.mutateAsync({
      listingId: listing.id,
      version: listing.version,
      action: "reject",
      reason,
      notifySupplier,
    });

    toast.success("Listing declined");
    navigate({
      to: "/admin/listing-review",
    });
  }

  async function handleApprove() {
    if (!listing) return;

    await reviewMut.mutateAsync({
      listingId: listing.id,
      version: listing.version,
      action: "approve",
      cited_registration: citedRegistration,
      reason: undefined,
      salePrices: prices?.map(p => {
        return {
          currency: p.salePriceCurrency as ReviewSalePrice["currency"],
          market_id: p.marketId as ReviewSalePrice["market_id"],
          sale_price: p.salePriceCents,
          variant_id: p.variantId,
        } satisfies ReviewSalePrice;
      }),
      notifySupplier,
    });

    toast.success("Listing approved");
    navigate({
      to: "/admin/listing-review",
    });
  }

  return (
    <PageWrapper
      title={
        <Flex css={{ gap: 8, items: "baseline" }}>
          Review Listing{" "}
          {params.version === 1 ? (
            <Badge color="red" css={{ fontSize: "1em", height: "1em" }}>
              NEW
            </Badge>
          ) : (
            <Span css={{ fontScale: "md", fontWeight: "normal", fontFamily: fonts.switzer }}>
              (<Span css={{ backgroundColor: colors.blue100 }}>blue</Span> = new)
            </Span>
          )}
          {listing.eap_available && (
            <Badge color="green" css={{ fontSize: "1em", height: "1em" }}>
              EAP
            </Badge>
          )}
        </Flex>
      }
      rightContent={
        <Text css={{ fontScale: "md", fontWeight: "normal", mb: 8 }}>
          Submitted by <Span css={{ fontWeight: 500 }}>{listing.updated_by.name}</Span>
        </Text>
      }
    >
      <ListingDiffViewer
        listing={listing}
        previousListing={hasPreviousVersion ? previousListing : listing}
        supplier={supplier}
        onPricesUpdated={handlePricesUpdated}
        isReadonly={isReadonly}
      />
      {!isReadonly && register && (
        <RegistrationForm
          citedRegistration={citedRegistration}
          onCitedRegistrationChange={setCitedRegistration}
          register={register}
        />
      )}
      {!isReadonly && (
        <>
          <Flex css={{ items: "center", justify: "center", gap: 8, mt: 16 }}>
            <input
              type="checkbox"
              id="notify-supplier"
              checked={notifySupplier}
              onChange={e => setNotifySupplier(e.target.checked)}
            />
            <Label htmlFor="notify-supplier" css={{ mb: 0, cursor: "pointer" }}>
              Notify supplier of review outcome
            </Label>
          </Flex>
          <Flex css={{ items: "baseline", justify: "center", gap: 16, mt: 8 }}>
            <Button
              variant="filled"
              color="red"
              leftIcon={<CloseIcon width="14" height="14" />}
              onClick={() => setDeclineModalOpen(true)}
              disabled={reviewMut.isPending}
            >
              Decline
            </Button>
            <Button
              variant="filled"
              onClick={handleApprove}
              color="green"
              leftIcon={<CheckIcon width="14" height="14" />}
              disabled={reviewMut.isPending}
            >
              Approve
            </Button>

            <DeclineListingModal
              isOpen={declineModalOpen}
              onOpenChange={setDeclineModalOpen}
              onConfirmed={handleDeclineConfirmed}
            />
          </Flex>
        </>
      )}
    </PageWrapper>
  );
}

type ListingDiffViewerProps = {
  listing: GetSupplierListingResponse;
  previousListing?: GetSupplierListingResponse;
  supplier: GetSupplierResponse;
  register?: Register;
  onPricesUpdated: (variantId: string, prices: DisplayPrice[]) => void;
  isReadonly: boolean;
};
function ListingDiffViewer({
  listing,
  previousListing,
  supplier,
  register,
  onPricesUpdated,
  isReadonly,
}: ListingDiffViewerProps) {
  const { data: hierarchy } = useSuspenseQuery(
    listCategoriesQuery(config.features.eapCategories && listing.eap_available ? "eap" : "listing"),
  );
  const { data: settings } = useSuspenseQuery(settingsQuery(listing.supplier_id));
  const pricingConfig = settings.pricing_config;

  const subcategory =
    hierarchy.subcategories.find(c => c.category_id === listing.category_id) ||
    hierarchy.categories.find(c => c.category_id === listing.category_id);
  const category = hierarchy.categories.find(c => c.category_id === subcategory?.parent_category_id);
  const hasProducts = listing.variants.some(v => v.type_id === "product");
  const tagsQuery = useQuery(tagSetQuery(TagSet.SupportType));

  const variantGroupNames: string[] = [];
  for (const variant of listing.variants) {
    const groupName = variant.variant_group || "";
    if (variantGroupNames.includes(groupName)) continue;
    variantGroupNames.push(groupName);
  }

  return (
    <>
      <Card css={{ flex: "1 0 0px", maxW: 1400 }}>
        <Box css={{ spaceY: 24 }}>
          <Grid css={{ gridCols: 3, gap: 16 }}>
            {listing.media.map((media, i) => (
              <Img
                key={media.media_id}
                css={{
                  width: "100%",
                  aspectRatio: 16 / 9,
                  objectFit: "cover",
                  border: `1px solid ${colors.outline}`,
                  borderRadius: 8,
                  boxShadow: diff(media.url, previousListing?.media[i]?.url) ? `0 0 0 3px ${colors.blue500}` : "none",
                }}
                src={media.url}
              />
            ))}
          </Grid>

          <Box>
            <Text css={{ fontScale: "2xs" }}>{supplier.name}</Text>
            <Heading
              css={{
                mt: 4,
                mb: 8,
                fontScale: "2xl",
                backgroundColor: diff(listing.title, previousListing?.title) ? colors.blue100 : undefined,
              }}
            >
              {listing.title}
            </Heading>
            <Box css={{ fontScale: "sm", color: colors.text2, spaceY: 4 }}>
              {register && (
                <Flex css={{ gap: 8, items: "center" }}>
                  <CertificateIcon />
                  <Text
                    css={{
                      backgroundColor: diff(
                        (listing.registration?.register_id || "") +
                          (listing.registration?.external_reference || "") +
                          (listing.registration?.registrant_name || ""),
                        (previousListing?.registration?.register_id || "") +
                          (previousListing?.registration?.external_reference || "") +
                          (previousListing?.registration?.registrant_name || ""),
                      )
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    {register.summary_template}
                  </Text>
                </Flex>
              )}
              <Flex css={{ gap: 8, items: "center" }}>
                <TagIcon width="14" height="14" />
                <Text
                  css={{
                    backgroundColor: diff(listing.title, previousListing?.title) ? colors.blue100 : undefined,
                  }}
                >
                  {category?.name ? `${category?.name} • ` : ""} {subcategory?.name}
                </Text>
              </Flex>
              {hasProducts && (
                <Flex css={{ gap: 8, items: "center" }}>
                  <TagIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(listing.delivery_method, previousListing?.delivery_method)
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    Delivery Method: {listing.delivery_method || "N/A"}
                  </Text>
                </Flex>
              )}
            </Box>
            <Box
              css={{
                mt: 16,
                fontScale: "sm",
                backgroundColor: diff(listing.description, previousListing?.description) ? colors.blue100 : undefined,
              }}
            >
              <Markdown markdown={listing.description} />
            </Box>
            {listing.eap_available && (
              <>
                <Flex
                  css={{
                    gap: 8,
                    flexDir: "column",
                    mt: 16,
                    justifySelf: "start",
                    minW: "50%",
                    border: `1px solid ${colors.outline}`,
                    p: 15,
                    rounded: 16,
                  }}
                >
                  <Text css={{ fontScale: "md", fontWeight: 500 }}>Support Types</Text>
                  <TagsDisplay tagIds={listing.tag_ids} tagsQuery={tagsQuery} />
                </Flex>
              </>
            )}
          </Box>

          <Box css={{ spaceY: 44 }}>
            {variantGroupNames.map(groupName => (
              <Box key={groupName} css={{ spaceY: 12 }}>
                <Heading>{groupName}</Heading>
                <Grid css={{ gridCols: 2, gap: 16 }}>
                  {listing.variants.map((variant, idx) => {
                    if ((variant.variant_group || "") !== groupName) {
                      return null;
                    }

                    const previousVariant = previousListing?.variants[idx];
                    return (
                      <VariantDiffInstance
                        key={variant.id}
                        listing={listing}
                        variant={variant}
                        previousVariant={previousVariant}
                        onPricesUpdated={prices => onPricesUpdated(variant.id, prices)}
                        isReadonly={isReadonly}
                        pricingConfig={pricingConfig}
                      />
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Box>

          <PostSalesMessages listing={listing} previousListing={previousListing} />
        </Box>
      </Card>
    </>
  );
}

/**
 * Every post-sales (automatic purchase instruction) message attached to the listing's variants, rendered in
 * full so the reviewer can read exactly what the customer will receive. Messages still containing square
 * brackets almost always mean the supplier left the suggested template placeholders in, so those get flagged.
 */
function PostSalesMessages({
  listing,
  previousListing,
}: {
  listing: GetSupplierListingResponse;
  previousListing?: GetSupplierListingResponse;
}) {
  const autoMessagesQ = useQuery(autoMessagesQuery(listing.supplier_id));
  const autoMessages = autoMessagesQ.data;

  const usedMessageIds: string[] = [];
  for (const variant of listing.variants) {
    if (variant.auto_message_id && !usedMessageIds.includes(variant.auto_message_id)) {
      usedMessageIds.push(variant.auto_message_id);
    }
  }
  const variantsWithoutMessage = listing.variants.filter(v => !v.auto_message_id);

  const usedMessages = usedMessageIds.map(id => ({
    id,
    message: autoMessages?.find(am => am.id === id),
    variants: listing.variants.filter(v => v.auto_message_id === id),
  }));
  const messagesWithBrackets = usedMessages.filter(m => m.message && containsSquareBrackets(m.message.message));

  return (
    <Box css={{ spaceY: 12 }}>
      <Heading>Post Sales Messages</Heading>

      {autoMessagesQ.isError && <Alert status="error">Couldn't load this supplier's post sales messages.</Alert>}

      {messagesWithBrackets.length > 0 && (
        <Alert status="warning">
          {messagesWithBrackets.length === 1
            ? "1 post sales message contains"
            : `${messagesWithBrackets.length} post sales messages contain`}{" "}
          square brackets. The supplier may have left template placeholders (e.g. [YOUR NAME]) in the message.
        </Alert>
      )}

      {variantsWithoutMessage.length > 0 && (
        <Alert status="warning">
          No post sales message set for: {variantsWithoutMessage.map(v => v.title).join(", ")}
        </Alert>
      )}

      {usedMessages.length === 0 && variantsWithoutMessage.length === 0 && (
        <Text css={{ fontScale: "sm", color: colors.text2 }}>This listing has no variants.</Text>
      )}

      {autoMessagesQ.isSuccess && usedMessages.length > 0 && (
        <Grid css={{ gridCols: 2, gap: 16 }}>
          {usedMessages.map(({ id, message, variants }) => {
            const isNewlyUsed = !previousListing?.variants.some(v => v.auto_message_id === id);
            return (
              <PostSalesMessageCard
                key={id}
                messageId={id}
                message={message}
                variantTitles={variants.map(v => v.title)}
                changed={isNewlyUsed}
              />
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

function PostSalesMessageCard({
  messageId,
  message,
  variantTitles,
  changed,
}: {
  messageId: string;
  message: AutoMessageWithAssociationsAndAttachments | undefined;
  variantTitles: string[];
  changed: boolean;
}) {
  const hasBrackets = message ? containsSquareBrackets(message.message) : false;

  return (
    <Flex
      css={{
        flexDir: "column",
        border: `1px solid ${hasBrackets ? colors.yellow600 : colors.outline}`,
        p: 15,
        rounded: 16,
        gap: 12,
      }}
    >
      <Flex css={{ gap: 8, items: "flex-start", justify: "space-between" }}>
        <Box>
          <Text css={{ fontScale: "md", weight: 500, backgroundColor: changed ? colors.blue100 : undefined }}>
            {message?.name ?? "Unknown message"}
          </Text>
          <Text css={{ fontScale: "xs", color: colors.text2 }}>
            {messageId}
            <br />
            Used by: {variantTitles.join(", ")}
          </Text>
        </Box>
        {hasBrackets && (
          <Badge color="yellow" css={{ flexShrink: 0 }}>
            Contains [brackets]
          </Badge>
        )}
      </Flex>

      {message ? (
        <Box css={{ fontScale: "sm", whiteSpace: "pre-wrap" }}>
          <Markdown markdown={message.message} />
        </Box>
      ) : (
        <Alert status="error">This message no longer exists for the supplier.</Alert>
      )}

      {message && message.attachments.length > 0 && (
        <Text css={{ fontScale: "xs", color: colors.text2 }}>
          Attachments: {message.attachments.map(a => a.file_name).join(", ")}
        </Text>
      )}
    </Flex>
  );
}

type VariantDiffInstanceProps = {
  listing: GetSupplierListingResponse;
  variant: GetSupplierListingResponse["variants"][number];
  previousVariant?: GetSupplierListingResponse["variants"][number];
  onPricesUpdated: (prices: DisplayPrice[]) => void;
  isReadonly: boolean;
  pricingConfig: PricingConfig;
};
function VariantDiffInstance({
  listing,
  variant,
  previousVariant,
  onPricesUpdated,
  isReadonly,
  pricingConfig,
}: VariantDiffInstanceProps) {
  const { data: locations } = useQuery(supplierLocationsQuery(listing.supplier_id));
  const location = locations?.find(loc => loc.location_id === variant.in_person_location_id);
  const { data: calendars } = useQuery(calendarModelsQuery(listing.supplier_id));
  const { data: autoMessages } = useQuery(autoMessagesQuery(listing.supplier_id));
  const autoMessage = autoMessages?.find(am => am.id === variant.auto_message_id);

  return (
    <>
      <Flex
        css={{
          flexDir: "column",
          border: `1px solid ${colors.outline}`,
          p: 15,
          rounded: 16,
          gap: 12,
        }}
      >
        <VariantTypeBadge variant={variant.type_id} diff={diff(variant.type_id, previousVariant?.type_id)} />
        <Flex css={{ gap: 36, flexDir: "row", items: "flex-start" }}>
          <Flex css={{ gap: 12, flexDir: "column" }}>
            <Text
              css={{
                fontScale: "md",
                weight: 500,
                backgroundColor: diff(variant.title, previousVariant?.title) ? colors.blue100 : undefined,
              }}
            >
              {variant.title}
            </Text>
            <Box css={{ fontScale: "sm", flexShrink: 0 }}>
              <Flex css={{ items: "center", gap: 6 }}>
                <EarthIcon width="14" height="14" />
                <Text
                  css={{
                    backgroundColor: diff(variant.location_type, previousVariant?.location_type)
                      ? colors.blue100
                      : undefined,
                  }}
                >
                  {describeLocationType(variant.location_type)}
                </Text>
              </Flex>
              {variant.in_person_location_id && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <LocationPinIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(variant.in_person_location_id, previousVariant?.in_person_location_id)
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    {location?.name || "Unknown Location"}
                  </Text>
                </Flex>
              )}
              {isBookingVariant(variant) && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <CalendarIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(
                        variant.calendars.map(c => c.calendar_id).join(","),
                        previousVariant && isBookingVariant(previousVariant)
                          ? previousVariant?.calendars.map(c => c.calendar_id).join(",")
                          : undefined,
                      )
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    Calendars:{" "}
                    {variant.calendars
                      .map(
                        variantCalendar =>
                          calendars?.find(dbCalendar => dbCalendar.id === variantCalendar.calendar_id)?.resourceName,
                      )
                      .join(", ")}
                  </Text>
                </Flex>
              )}
              {isEventVariant(variant) && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <CircleClockIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(
                        variant.start_timestamp,
                        previousVariant && isEventVariant(previousVariant)
                          ? previousVariant.start_timestamp
                          : undefined,
                      )
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    {formatDateTime(variant.start_timestamp)}
                  </Text>
                </Flex>
              )}
              {isBookingVariant(variant) && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <CircleClockIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(
                        variant.duration_minutes + ":" + variant.buffer_minutes,
                        previousVariant && isBookingVariant(previousVariant)
                          ? previousVariant.duration_minutes + ":" + previousVariant.buffer_minutes
                          : undefined,
                      )
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    {variant.duration_minutes} minutes ({variant.buffer_minutes || 0} minute buffer)
                  </Text>
                </Flex>
              )}
              {isSubscriptionVariant(variant) && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <CircleClockIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(
                        variant.subscription_month_frequency,
                        previousVariant && isSubscriptionVariant(previousVariant)
                          ? previousVariant.subscription_month_frequency
                          : undefined,
                      )
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    {variant.subscription_month_frequency} months
                  </Text>
                </Flex>
              )}
              {variant.requires_email && (
                <Flex css={{ items: "center", gap: 6 }}>
                  <MailIcon width="14" height="14" />
                  <Text
                    css={{
                      backgroundColor: diff(variant.requires_email, previousVariant?.requires_email)
                        ? colors.blue100
                        : undefined,
                    }}
                  >
                    Requires Email
                  </Text>
                </Flex>
              )}
              <Flex css={{ items: "center", gap: 6 }}>
                <ChatBubbleTextSquareIcon width="14" height="14" />
                <Text
                  css={{
                    backgroundColor: diff(variant.auto_message_id, previousVariant?.auto_message_id)
                      ? colors.blue100
                      : undefined,
                  }}
                >
                  Post sales message: {variant.auto_message_id ? (autoMessage?.name ?? "Unknown") : "None"}
                </Text>
              </Flex>
            </Box>
          </Flex>
          {!isOfferVariant(variant) && (
            <Flex css={{ gap: 12 }}>
              {/* --------------- */}
              <Flex css={{ flexDir: "column", gap: 0 }}>
                <Text
                  css={{
                    fontScale: "md",
                    weight: 500,
                  }}
                >
                  Pricing
                  <br />
                  <Span css={{ fontScale: "xs" }}>{variant.id}</Span>
                </Text>

                <PricingTable
                  listing={listing}
                  variant={variant}
                  previousVariant={previousVariant}
                  onPricesUpdated={onPricesUpdated} // Output only. The pricing table creates the initial state from the variant.
                  isReadonly={isReadonly}
                  pricingConfig={pricingConfig}
                />
              </Flex>
            </Flex>
          )}
          {isOfferVariant(variant) && (
            <Flex css={{ flexDir: "column", gap: 4 }}>
              <Text
                css={{
                  fontScale: "md",
                  weight: 500,
                }}
              >
                Offer URL
                <br />
                <Span css={{ fontScale: "xs" }}>{variant.id}</Span>
              </Text>
              <a
                href={variant.offer_url ?? undefined}
                target="_blank"
                rel="noreferrer"
                style={{ color: colors.blue500 }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = "underline")}
                onMouseOut={e => (e.currentTarget.style.textDecoration = "none")}
              >
                {variant.offer_url}
              </a>
            </Flex>
          )}
        </Flex>

        {variant.description && (
          <Box
            css={{
              fontScale: "sm",
              backgroundColor: diff(variant.description, previousVariant?.description) ? colors.blue100 : undefined,
            }}
          >
            <Markdown markdown={variant.description} />
          </Box>
        )}
      </Flex>
    </>
  );
}

type RegistrationFormProps = {
  citedRegistration: CitedRegistration | undefined;
  onCitedRegistrationChange: (registration: CitedRegistration) => void;
  register: Register;
};
function RegistrationForm({ register, citedRegistration, onCitedRegistrationChange }: RegistrationFormProps) {
  const membershipTypes = useQuery(registerMembershipTypesQuery(register.id));

  return (
    <Card
      css={{ mt: 16 }}
      title="Registration"
      description="This supplier has claimed they are registered, please verify."
    >
      {citedRegistration && (
        <Flex css={{ flexDir: "column", gap: 12 }}>
          <Flex css={{ gap: 16 }}>
            <Field>
              <Label>Register</Label>
              <Flex css={{ gap: 8, items: "center" }}>
                <Input
                  value={register?.name}
                  disabled
                  readOnly
                  css={{
                    "& input:disabled": { borderColor: colors.outline2 },
                  }}
                />
                {register?.url && (
                  <Anchor href={register?.url} target="_blank" rel="noreferrer">
                    <Span css={{ color: colors.blue500, textDecoration: "underline" }}>Link to register</Span>
                  </Anchor>
                )}
              </Flex>
            </Field>
            {membershipTypes.data && membershipTypes.data.length > 0 && (
              <Field>
                <Label>Membership Type</Label>
                <Select
                  value={citedRegistration.membership_type_id || ""}
                  onValueChange={value => {
                    onCitedRegistrationChange({ ...citedRegistration, membership_type_id: value });
                  }}
                >
                  <option value="" disabled>
                    Select membership type
                  </option>
                  {membershipTypes.data?.map(membershipType => (
                    <option
                      key={membershipType.membership_type_id}
                      value={membershipType.membership_type_id}
                      disabled={!membershipType.allowed}
                    >
                      {membershipType.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </Flex>
          <Grid css={{ gridCols: 1, gap: 16, "@sm": { gridCols: 2 } }}>
            <Field>
              <Label>Name</Label>
              <Input
                value={citedRegistration.registrant_name}
                onValueChange={value => onCitedRegistrationChange({ ...citedRegistration, registrant_name: value })}
              />
            </Field>
            <Field>
              <Label>Registration No.</Label>
              <Input
                value={citedRegistration.external_reference}
                onValueChange={value => onCitedRegistrationChange({ ...citedRegistration, external_reference: value })}
              />
            </Field>
            <Field>
              <Label>Expiry Date</Label>

              <Input
                type="date"
                value={citedRegistration.expiry_date ?? new Date().toISOString().slice(0, 10)}
                min={toDateOnly(new Date()).toString()}
                onValueChange={value => onCitedRegistrationChange({ ...citedRegistration, expiry_date: value })}
              />
            </Field>
            <Field>
              <Label>Byline for Marketplace (don't edit unless you have to)</Label>
              <Input
                value={citedRegistration?.summary}
                className="ph-clear"
                onValueChange={value => onCitedRegistrationChange({ ...citedRegistration, summary: value })}
              />
            </Field>
          </Grid>
        </Flex>
      )}
    </Card>
  );
}

type DisplayPrice = {
  marketId: string;
  costPriceCents: number;
  costPriceCurrency: string;
  compareAtCents?: number | null;
  salePriceCents: number;
  salePriceCurrency: string;
  editable: boolean;
  tokens?: number;
  variantId: string;
};

type PricingTableProps = {
  listing: GetSupplierListingResponse;
  variant: SupplierListingVariant;
  previousVariant?: SupplierListingVariant;
  onPricesUpdated: (prices: DisplayPrice[]) => void;
  isReadonly: boolean;
  pricingConfig: PricingConfig;
};
function PricingTable({
  listing,
  variant,
  previousVariant,
  onPricesUpdated,
  isReadonly,
  pricingConfig,
}: PricingTableProps) {
  const [prices, setPrices] = useState<DisplayPrice[]>(() => {
    const result: DisplayPrice[] = [];
    const knownTokenCurrencyCodes = getTokenCurrencyCodes();
    for (const price of variant.prices) {
      if (isMarketplaceVariantPrice(price)) {
        // Marketplace.
        const tokenCurrencyCode = price.price_currency_code as TokenCurrencyCode;
        if (!knownTokenCurrencyCodes.includes(tokenCurrencyCode)) continue;

        const commissionRate = isCommissionPricingConfig(pricingConfig)
          ? pricingConfig.percent / 100
          : DEFAULT_COMMISSION_RATE; // 0.15 === 15%

        const saleMoney = tokensToMoney({
          amount: price.price_tokens,
          tokenCurrencyCode,
        });
        const salePriceCents = dollarsToCents(saleMoney.amount, saleMoney.currencyCode);

        result.push({
          marketId: price.market_id,
          costPriceCents: Math.round(salePriceCents * (1 - commissionRate) * 100) / 100,
          salePriceCents,
          compareAtCents:
            price.compare_at_tokens == null
              ? null
              : dollarsToCents(
                  tokensToMoney({
                    amount: price.compare_at_tokens,
                    tokenCurrencyCode,
                  }).amount,
                  saleMoney.currencyCode,
                ),
          costPriceCurrency: saleMoney.currencyCode,
          salePriceCurrency: saleMoney.currencyCode,
          editable: false,
          tokens: price.price_tokens,
          variantId: variant.id,
        });
      } else if (isEapVariantPrice(price)) {
        // EAP.
        const costCents = price.cost_price;
        const saleCents = price.sale_price === 0 ? calculateSalePrice(costCents, price.currency) : price.sale_price;
        result.push({
          marketId: price.market_id,
          costPriceCents: costCents,
          salePriceCents: saleCents,
          costPriceCurrency: price.currency,
          salePriceCurrency: price.currency,
          editable: !isReadonly,
          variantId: variant.id,
        });
      } else {
        throw new Error("Unknown price type " + (price as VariantPrice).$type);
      }
    }
    onPricesUpdated(result); // Since this component creates the initial prices, we have to notify top level of initial state.
    return result;
  });
  return (
    <>
      <Table css={{ mt: 6 }}>
        <Thead>
          <Tr>
            <Th>Market</Th>
            <Th css={{ textAlign: "center" }}>Price</Th>
            <Th css={{ textAlign: "center" }}>Supplier Gets</Th>
          </Tr>
        </Thead>
        <Tbody>
          {prices.map(price => {
            function handlePriceUpdate(marketId: string, value: number) {
              const newPrices = [...prices];
              const existingPrice = newPrices.find(p => p.marketId === marketId);
              const existingPriceIndex = newPrices.findIndex(p => p.marketId === marketId);
              if (existingPrice) {
                newPrices[existingPriceIndex] = {
                  ...existingPrice,
                  salePriceCents: value,
                } satisfies DisplayPrice;
                setPrices(newPrices);
                onPricesUpdated(newPrices);
              }
            }

            return (
              <Tr key={price.marketId}>
                <Td>{price.marketId}</Td>
                <Td css={{ textAlign: "right" }}>
                  {price.editable ? (
                    <SalesPriceInput price={price} onChange={(marketId, value) => handlePriceUpdate(marketId, value)} />
                  ) : (
                    <Text css={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                      {formatCents(price.salePriceCents, price.salePriceCurrency)}{" "}
                      {price.compareAtCents && (
                        <Text
                          as="span"
                          css={{
                            textDecoration: "line-through",
                            weight: "normal",
                            color: colors.text2,
                            fontScale: "xs",
                          }}
                        >
                          {formatCents(price.compareAtCents, price.salePriceCurrency)}
                        </Text>
                      )}
                    </Text>
                  )}
                </Td>
                <Td css={{ color: colors.text2, textAlign: "right" }}>
                  {formatCents(price.costPriceCents, price.costPriceCurrency)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
}

type SalesPriceInputProps = {
  price: DisplayPrice;
  onChange: (marketId: string, value: number) => void;
};
function SalesPriceInput({ price, onChange }: SalesPriceInputProps) {
  const dollars = price.salePriceCents == null ? 0 : centsToDollars(price.salePriceCents, price.salePriceCurrency);
  const defaultDollarsStr =
    price.salePriceCents == null ? "" : dollars.toFixed(getCurrencyDecimalPlaces(price.salePriceCurrency));
  const [localDollarsStr, setLocalDollarsStr] = useState<string | undefined>(undefined);

  const dollarsStr = localDollarsStr ?? defaultDollarsStr;

  function handlePriceUpdated(value: string) {
    setLocalDollarsStr(value);
  }

  function handlePriceCommitted(str: string) {
    if (str !== "") {
      const dollars = parseFloat(str);
      const cents = dollarsToCents(dollars, price.salePriceCurrency);
      onChange(price.marketId, cents);
    }
    setLocalDollarsStr(undefined);
  }

  return (
    <Input
      value={dollarsStr}
      onValueChange={handlePriceUpdated}
      onValueCommit={handlePriceCommitted}
      css={{
        width: 90,
        textAlign: "right",
      }}
      size="sm"
      step={0.01}
      type="text"
      inputMode="numeric"
      autoFocus
    />
  );
}

const Anchor = styled("a", { flexShrink: 0 });

/**
 * Returns true if a and b are different, false otherwise.
 *
 * Does a shallow comparison of objects, and an `Object.is` comparison of other types.
 */
/**
 * True if the message still has square brackets in it, ignoring markdown links (`[text](url)`), which are
 * legitimate. Anything else in brackets is most likely a leftover placeholder from the suggested templates.
 */
function containsSquareBrackets(message: string): boolean {
  const withoutLinks = message.replace(/\[[^\]]*\]\([^)]*\)/g, "");
  return /[[\]]/.test(withoutLinks);
}

function diff(a: any, b: any) {
  if (typeof a !== "object" || typeof b !== "object") {
    return !Object.is(a, b);
  }
  // `typeof null === 'object'`, yet `Object.keys(null)` throws an error >:(
  if (a == null || b == null) {
    return !Object.is(a, b);
  }

  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (!Object.is(a[key], b[key])) {
      return true;
    }
  }

  return false;
}

function supplierRegistrationToCitedRegistration(
  registration: NonNullable<GetSupplierListingResponse["registration"]>,
  register: Register,
): CitedRegistration {
  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  return {
    register_id: registration.register_id,
    external_reference: registration.external_reference,
    registrant_name: registration.registrant_name,
    admin_notes: register.summary_template,
    membership_type_id: registration.membership_type_id || null,
    expiry_date: registration.expiry_date ?? toDateOnly(nextYear),
    summary: register.summary_template,
  };
}

function describeLocationType(location_type: SupplierListingVariant["location_type"]): string {
  switch (location_type) {
    case "in_person":
      return "In Person";
    case "online":
      return "Online";
    case "getaway":
      return "Getaway";
    default:
      return "";
  }
}

const cellStyles = {
  textAlign: "left",
  fontScale: "sm",
  py: 4,
  px: 10,
  borderBottom: `1px solid ${colors.outline}`,
  "&:where(:first-child)": {
    pl: 0,
  },
  "&:last-child": {
    pr: 0,
  },
} satisfies CSS;

const Th = styled("th", {
  ...cellStyles,
  fontScale: "xs",
  color: colors.text2,
  weight: 500,
  py: 4,
});
const Td = styled("td", cellStyles);
